/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { ChangeEvent, ReactNode, Ref, useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from '@mui/styles'
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  AutocompleteRenderGetTagProps,
  Chip,
  createFilterOptions,
  TextField,
  Typography,
} from '@mui/material'
import { Variant } from '../../types'
import { nanoid } from 'nanoid'

type MaybeBoolean = boolean | undefined

type Props<T> = {
  error?: boolean
  errors?: boolean[]
  label?: string
  helperText?: string
  name?: string
  // T | (T | string)[] | string | null
  value: any
  renderInput?: ((params: AutocompleteRenderInputParams) => ReactNode) | null | undefined
  variant?: Variant
  disableFilter?: boolean
  autoFocus?: boolean
  max?: number // max number of selection when in multi mode
  count?: number // manually pass in current count
  onChange: (event: ChangeEvent<any>, value: any, reason: AutocompleteChangeReason) => void
  getKey?: (value: T) => string
  mRef?: Ref<HTMLElement>
  placeholder?: string
} & Omit<AutocompleteProps<T, MaybeBoolean, MaybeBoolean, MaybeBoolean, any>, 'onChange' | 'renderInput' | 'id'>

const useStyles = makeStyles({
  option: {
    '&[data-focus="true"]': { backgroundColor: '#5789ff', color: 'white' },
  },
})

export default function InputSearchDropdown<T>(props: Props<T>): JSX.Element {
  const {
    mRef,
    autoHighlight = true,
    clearOnEscape = true,
    count,
    disableFilter,
    error,
    errors,
    filterOptions,
    filterSelectedOptions = true,
    getKey,
    label,
    placeholder,
    helperText,
    name,
    onChange,
    openOnFocus = true,
    renderInput = null,
    renderOption,
    renderTags,
    selectOnFocus = true,
    variant = 'outlined',
    multiple,
    max,
    autoFocus,
    size = 'small',
    ...restOfProps
  } = props
  const id = useRef(nanoid())
  const [value, setValue] = useState<any>(null)
  const mainStyles = useStyles()
  const getOptionLabel = useCallback(
    (option: T | string): string => {
      if (typeof option === 'string') {
        return option
      }

      if (!props.getOptionLabel) {
        throw new Error('You must provide the getOptionLabel prop to the InputSearchDropdown component')
      }

      return props.getOptionLabel(option)
    },
    [props.getOptionLabel]
  )
  const getKeySuffix = useCallback(
    (option: T | string) => {
      return typeof option === 'string' ? option : getKey !== undefined ? getKey(option) : getOptionLabel(option)
    },
    [getOptionLabel, getKey]
  )
  const getCount = useCallback((): number => {
    if (count !== undefined) {
      return count
    }

    return (value as any[])?.length || 0
  }, [count, value])
  const getValue = useCallback((): any => {
    if (multiple && value === null) {
      return []
    }

    if (multiple && !Array.isArray(value)) {
      return [value]
    }

    if (!multiple && Array.isArray(value)) {
      return value.length ? value[0] : null
    }

    return value
  }, [multiple, value])
  const renderDefaultOption = useCallback(
    (props: object, option: T) => {
      return (
        <li {...props} key={`option-${getKeySuffix(option)}`}>
          {getOptionLabel(option)}
        </li>
      )
    },
    [getKeySuffix, getOptionLabel]
  )
  const renderDefaultTags = useCallback(
    (values: (T | string)[], getTagProps: AutocompleteRenderGetTagProps) => {
      return values.map((value, index) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { key, ...tagProps } = getTagProps({ index })
        return (
          <Chip
            key={`chip-${getKeySuffix(value)}`}
            css={css`
              border-color: ${errors && errors[index] ? 'red' : 'initial'};
            `}
            variant="outlined"
            label={getOptionLabel(value)}
            size="small"
            {...tagProps}
          />
        )
      })
    },
    [getKeySuffix, getOptionLabel, errors]
  )
  const renderDefaultInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return (
        <TextField
          {...params}
          error={error || !!errors?.length}
          label={label}
          placeholder={placeholder}
          variant={variant as any}
          autoFocus={autoFocus}
        />
      )
    },
    [error, errors, label, variant, autoFocus]
  )

  useEffect(() => {
    if (props.value !== value) {
      if (!props.value && props.multiple) {
        setValue([])
      }
      setValue(props.value)
    }
  }, [props.value]) // eslint-disable-line

  function handleOnChange(_: ChangeEvent<any>, newValue: any, reason: AutocompleteChangeReason): void {
    // logic that prevents the input from exceeding its limit
    if (multiple && max) {
      if (count !== undefined) {
        const delta = count - value.length
        if (newValue.length + delta > max) {
          return
        }
      } else if (newValue.length > max) {
        return
      }
    }

    setValue(newValue)
    onChange({ target: { name, value: newValue } } as any, newValue, reason)
  }

  function handleOptionFilter(): any {
    return createFilterOptions<T>({
      matchFrom: 'any',
      stringify: getOptionLabel,
    })
  }

  return (
    <div>
      <Autocomplete
        id={id.current}
        css={css`
          // TODO: this is due to a bug in MaterialUI clear up when resolve -- should file a PR ticket
          &&& .MuiInputBase-root {
            padding-right: 39px;
          }
        `}
        autoHighlight={autoHighlight}
        clearOnEscape={clearOnEscape}
        filterOptions={!disableFilter ? filterOptions || handleOptionFilter() : (options: any): any => options}
        filterSelectedOptions={filterSelectedOptions}
        onChange={handleOnChange}
        openOnFocus={openOnFocus}
        renderInput={renderInput || renderDefaultInput}
        renderOption={renderOption ? renderOption : renderDefaultOption}
        renderTags={renderTags || renderDefaultTags}
        selectOnFocus={selectOnFocus}
        multiple={multiple}
        classes={mainStyles}
        size={size}
        {...restOfProps}
        value={getValue()}
        ref={mRef}
      />
      <div>
        {helperText && (
          <Typography
            css={css`
              float: left;
              margin-left: 14px;
              margin-top: 4px;
              color: ${error ? 'red' : 'rgba(0, 0, 0, 0.54)'};
              width: calc(100% - 82px);
            `}
            variant="caption"
          >
            {helperText}
          </Typography>
        )}
        {multiple && max && (
          <Typography
            css={css`
              font-size: 0.75rem;
              text-align: right;
              float: right;
              margin-left: 14px;
              margin-right: 14px;
              margin-top: 4px;
              color: ${error ? 'red' : 'rgba(0, 0, 0, 0.54)'};
              width: 40px;
            `}
            variant="subtitle1"
          >
            {getCount()}/{max}
          </Typography>
        )}
      </div>
    </div>
  )
}
